import { useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import CheckMark from '@/app/components/questionnaireV2/icons/check-mark';
import { ChevronDownAnimated } from '@/components/icons/chevron-down-animated';

interface Option {
  value: string;
  label: string;
}

interface DropdownSelectProps {
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

export default function DropdownSelect({
  options,
  value,
  onChange,
  placeholder = 'Sort Cancers By',
  className = '',
}: DropdownSelectProps) {
  const [isOpen, setIsOpen] = useState(false);

  const selectedOption = options.find((option) => option.value === value);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  return (
    <div className={`relative ${className}`} style={{ width: '183px' }}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-flex h-9 flex items-center justify-center px-4 py-2 rounded-[8px] border border-solid border-[rgba(0,0,0,0.1)] transition-colors duration-200 ${
          isOpen
            ? 'bg-[var(--color-catch-dark-blue-90)] text-white'
            : 'bg-white text-[#183344] hover:bg-gray-50'
        }`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="text-[16px] text-center whitespace-nowrap font-semibold leading-[1.3]">
          {selectedOption?.label || placeholder}
        </span>
        <ChevronDownAnimated isOpen={isOpen}/>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
            className="absolute z-50 w-full mt-1 bg-white border border-[rgba(0,0,0,0.1)] rounded-[8px] shadow-lg overflow-hidden"
          >
            <div className="py-2" role="listbox">
              {options.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleSelect(option.value)}
                  className={`w-full px-4 py-2 transition-colors duration-150 text-[16px] font-semibold leading-[1.3] ${
                    option.value === value
                      ? 'text-[#183344]'
                      : 'text-[#183344] hover:bg-gray-50'
                  } flex items-center justify-center relative`}
                  role="option"
                  aria-selected={option.value === value}
                >
                  <div className="absolute left-4 w-[18px] h-[18px] flex items-center justify-center">
                    {option.value === value && (
                      <CheckMark/>
                    )}
                  </div>
                  <span className="text-center">{option.label}</span>
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
